import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { VisibilityBadge } from 'components/blocks/visibility-field';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import {
  useDisconnectOpenConnectorConnection,
  useGetComposioToolkitCatalog,
  useGetOpenConnectorCatalog,
  useGetOpenConnectorConnections,
  useGetToolById,
  useInitiateOpenConnectorConnection,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { cn } from 'lib/utils';
import { resolveToolProviderIcon } from 'pages/agents/utils/agent-tool-provider-icons';
import { connectorCatalogLogo } from './catalog-tool-logo';
import {
  getOpenConnectorConfig,
  getToolEditSubject,
  getToolVisibility,
  normalizeToolDetails,
} from './tool-detail-utils';

const PENDING_TIMEOUT_MS = 3 * 60 * 1000;
const VISIBLE_ACTIONS_COUNT = 7;
const MIN_DESCRIPTION_LENGTH = 24;

type OpenConnectorDetailsModalProps = {
  tool: ITool | null;
  projectKey: string;
  onClose: () => void;
};

function formatLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatActionLabel(actionId: string, service: string): string {
  const prefix = `${service}.`;
  return actionId.startsWith(prefix) ? actionId.slice(prefix.length) : actionId;
}

function resolveDescription(toolDescription?: string, catalogDescription?: string | null): string | null {
  const tool = toolDescription?.trim() ?? '';
  const catalog = catalogDescription?.trim() ?? '';

  if (catalog.length >= MIN_DESCRIPTION_LENGTH) return catalog;
  if (tool.length >= MIN_DESCRIPTION_LENGTH) return tool;
  if (catalog.length > 0) return catalog;
  if (tool.length >= 10) return tool;

  return null;
}

function ActionChip({ label }: Readonly<{ label: string }>) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted/70 px-2.5 py-1 text-xs text-foreground/85">
      <Check className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
      <span className="truncate font-mono lowercase">{label}</span>
    </span>
  );
}

function ConnectorDetailsSkeleton() {
  return (
    <div className="space-y-6 p-7" aria-busy="true" aria-live="polite">
      <div className="flex items-start gap-4 pr-8">
        <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-7 w-20 shrink-0 rounded-full" />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-[92%]" />
        <Skeleton className="h-4 w-4/5" />
      </div>

      <div className="space-y-3">
        <Skeleton className="h-4 w-36" />
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-7 w-28 rounded-full" />
          <Skeleton className="h-7 w-36 rounded-full" />
          <Skeleton className="h-7 w-24 rounded-full" />
          <Skeleton className="h-7 w-32 rounded-full" />
        </div>
      </div>

      <div className="space-y-4 border-t border-border/70 pt-5">
        <Skeleton className="h-4 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>

      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
      </div>
    </div>
  );
}

function ConnectorLogo({
  logo,
  name,
}: Readonly<{
  logo?: string | null;
  name: string;
}>) {
  if (logo) {
    return (
      <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-grey-100 bg-white p-0.5 shadow-sm dark:border-border dark:bg-card">
        <img src={logo} alt="" className="h-full w-full rounded-full object-contain" />
      </span>
    );
  }

  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-grey-100 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground dark:border-border">
      {name.slice(0, 2)}
    </span>
  );
}

function inferServiceFromActions(actions: string[] | null | undefined): string {
  const first = actions?.[0];
  if (!first?.includes('.')) return '';
  return first.split('.')[0] ?? '';
}

export function OpenConnectorDetailsModal({
  tool,
  projectKey,
  onClose,
}: Readonly<OpenConnectorDetailsModalProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAllActions, setShowAllActions] = useState(false);
  const [pendingService, setPendingService] = useState<string | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const popupRef = useRef<Window | null>(null);

  const toolId = tool?.id ?? '';
  const isOpen = Boolean(tool);

  const toolQuery = useGetToolById({ toolId, projectKey }, { enabled: isOpen && !!projectKey });

  const toolDetails = useMemo(
    () => normalizeToolDetails(toolQuery.data, tool),
    [tool, toolQuery.data]
  );

  const { services, actions: allowlistedActions } = getOpenConnectorConfig(toolDetails ?? tool);
  const primaryService =
    services[0] ?? inferServiceFromActions(allowlistedActions) ?? '';

  const catalogQuery = useGetOpenConnectorCatalog(primaryService || undefined, {
    enabled: isOpen && !!primaryService,
  });
  const toolkitCatalogQuery = useGetComposioToolkitCatalog({ enabled: isOpen });
  const connectionsQuery = useGetOpenConnectorConnections(toolId, {
    enabled: isOpen && !!toolId,
    refetchOnWindowFocus: true,
    refetchInterval: pendingService ? 2000 : false,
  });

  const initiateMutation = useInitiateOpenConnectorConnection();
  const disconnectMutation = useDisconnectOpenConnectorConnection();

  const isOwner = useIsOwner(getToolEditSubject(toolQuery.data, toolDetails ?? tool));
  const isPublicTool = getToolVisibility(toolDetails ?? tool) === 'public';
  const canManageConnection = isPublicTool || isOwner;

  const catalogEntry = useMemo(() => {
    if (!primaryService) return null;
    return (
      catalogQuery.data?.services.find((entry) => entry.service === primaryService) ?? null
    );
  }, [catalogQuery.data, primaryService]);

  const connections = connectionsQuery.data?.connections ?? {};
  const connectionEntry = primaryService ? connections[primaryService] : undefined;
  const isConnected = connectionEntry?.connected === true;

  const isPendingThis = pendingService === primaryService;
  const busy =
    (initiateMutation.isPending || disconnectMutation.isPending) &&
    (initiateMutation.variables?.service === primaryService ||
      disconnectMutation.variables?.service === primaryService);

  const actionLabels = useMemo(() => {
    if (!allowlistedActions?.length) return [];
    return allowlistedActions.map((actionId) => formatActionLabel(actionId, primaryService));
  }, [allowlistedActions, primaryService]);

  const visibleActions = showAllActions
    ? actionLabels
    : actionLabels.slice(0, VISIBLE_ACTIONS_COUNT);
  const hasMoreActions = actionLabels.length > VISIBLE_ACTIONS_COUNT;

  const appName = catalogEntry?.displayName || formatLabel(primaryService) || '';
  const displayName = toolDetails?.name || tool?.name || appName;

  // Open-connector catalog often has no iconUrl (OneDrive). The tools grid already
  // resolves that mark from the toolkit catalog; use the same logo, then a local asset.
  const logoSource = toolDetails ?? tool;
  const isToolkitCatalogSettled =
    toolkitCatalogQuery.isError || (!toolkitCatalogQuery.isPending && !toolkitCatalogQuery.isFetching);
  let logo: string | null = catalogEntry?.iconUrl || null;
  if (!logo && logoSource) {
    logo = connectorCatalogLogo(logoSource, toolkitCatalogQuery.data?.toolkits ?? [], primaryService);
  }
  if (!logo && isToolkitCatalogSettled) {
    logo = resolveToolProviderIcon(primaryService);
  }
  const displayDescription = resolveDescription(toolDetails?.description, null);
  const categoryLabel = catalogEntry?.categories?.[0]?.displayName
    ? formatLabel(catalogEntry.categories[0].displayName)
    : t('CONNECTOR', { defaultValue: 'Connector' });

  const actionsHeading = allowlistedActions?.length
    ? t('ACTIONS_ENABLED', {
        defaultValue: '{{count}} actions enabled',
        count: allowlistedActions.length,
      })
    : null;

  const clearPending = () => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    setPendingService(null);
    popupRef.current = null;
  };

  useEffect(() => {
    if (!isOpen) {
      setShowAllActions(false);
      clearPending();
    }
  }, [isOpen]);

  useEffect(() => () => clearPending(), []);

  useEffect(() => {
    // console.log('pendingService', pendingService); 
    if (!pendingService) return;
    const entry = connections[pendingService];
    // console.log('pendingService entry', entry);
    if (entry?.connected) {
      clearPending();
      toast({
        variant: 'success',
        title: t('CONNECTED', { defaultValue: 'Connected' }),
        description: entry.accountLabel
          ? t('ACCOUNT_CONNECTED_AS', {
              defaultValue: 'Connected as {{account}}.',
              account: entry.accountLabel,
            })
          : t('ACCOUNT_CONNECTED_SUCCESSFULLY', {
              defaultValue: 'Account connected successfully.',
            }),
      });
      void queryClient.invalidateQueries({
        queryKey: ['tools', 'open-connector-connections', toolId],
      });
    }
  }, [connections, pendingService, toolId, queryClient, toast, t]);

  useEffect(() => {
    if (!pendingService || !popupRef.current) return;

    const interval = window.setInterval(() => {
      if (popupRef.current?.closed) {
        window.clearInterval(interval);
        void queryClient.invalidateQueries({
          queryKey: ['tools', 'open-connector-connections', toolId],
        });
      }
    }, 1000);

    return () => window.clearInterval(interval);
  }, [pendingService, toolId, queryClient]);

  const handleConnect = async () => {
    if (!canManageConnection || !primaryService) return;

    try {
      const popup = window.open('about:blank', 'oc-connect', 'width=520,height=680');
      popupRef.current = popup;

      const response = await initiateMutation.mutateAsync({ toolId, service: primaryService });
      if (!response?.redirectUrl) {
        popup?.close();
        throw new Error(t('NO_REDIRECT_URL', { defaultValue: 'No authorization URL returned.' }));
      }

      if (popup) {
        popup.location.href = response.redirectUrl;
      } else {
        window.open(response.redirectUrl, 'oc-connect', 'width=520,height=680');
      }

      setPendingService(primaryService);
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = setTimeout(() => clearPending(), PENDING_TIMEOUT_MS);
    } catch (error) {
      popupRef.current?.close();
      popupRef.current = null;
      toast({
        variant: 'destructive',
        title: t('CONNECTION_FAILED', { defaultValue: 'Connection failed' }),
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_START_AUTHORIZATION', {
                defaultValue: 'Failed to start the authorization flow.',
              }),
      });
    }
  };

  const handleDisconnect = async () => {
    if (!canManageConnection || !primaryService) return;

    try {
      await disconnectMutation.mutateAsync({ toolId, service: primaryService });
      toast({
        variant: 'success',
        title: t('DISCONNECTED', { defaultValue: 'Disconnected' }),
        description: t('OPEN_CONNECTOR_ACCOUNT_DISCONNECTED', {
          defaultValue: 'Your account has been disconnected.',
        }),
      });
      await queryClient.invalidateQueries({
        queryKey: ['tools', 'open-connector-connections', toolId],
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('DISCONNECT_FAILED', { defaultValue: 'Disconnect failed' }),
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_DISCONNECT', { defaultValue: 'Failed to disconnect the account.' }),
      });
    }
  };

  const connectionButtonClassName = cn(
    'h-7 shrink-0 rounded-full border border-border/70 bg-muted/50 px-3 text-xs font-medium text-foreground shadow-none',
    'hover:bg-muted hover:text-foreground'
  );

  const renderConnectionButton = () => {
    if (!primaryService || !canManageConnection) return null;

    if (connectionsQuery.isPending || (connectionsQuery.isFetching && !connectionsQuery.data)) {
      return <Skeleton className="h-7 w-20 shrink-0 rounded-full" />;
    }

    if (isConnected) {
      return (
        <button
          type="button"
          className={cn(
            connectionButtonClassName,
            'disabled:pointer-events-none disabled:opacity-50',
            'hover:border-destructive/30 hover:bg-destructive/5 hover:text-destructive'
          )}
          disabled={busy}
          onClick={() => void handleDisconnect()}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            t('DISCONNECT', { defaultValue: 'Disconnect' })
          )}
        </button>
      );
    }

    const isConnecting = busy || isPendingThis;
    return (
      <button
        type="button"
        className={cn(
          connectionButtonClassName,
          'inline-flex min-w-20 items-center justify-center disabled:pointer-events-none disabled:opacity-50'
        )}
        disabled={isConnecting}
        onClick={() => void handleConnect()}
        aria-label={
          isConnecting
            ? t('CONNECTING', { defaultValue: 'Connecting' })
            : t('CONNECT', { defaultValue: 'Connect' })
        }
      >
        {isConnecting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
        ) : (
          t('CONNECT', { defaultValue: 'Connect' })
        )}
      </button>
    );
  };

  const showSkeleton = isOpen && !toolQuery.data && (toolQuery.isPending || toolQuery.isFetching);
  const isCatalogLoading = catalogQuery.isPending || (catalogQuery.isFetching && !catalogQuery.data);
  const isLogoPending =
    !logo &&
    (isCatalogLoading || toolkitCatalogQuery.isPending || toolkitCatalogQuery.isFetching);

  const renderActionsContent = () => {
    if (actionLabels.length === 0) {
      return (
        <p className="text-sm leading-6 text-muted-foreground">
          {t('NO_ACTIONS_FOUND', { defaultValue: 'No actions found.' })}
        </p>
      );
    }

    return (
      <>
        <div className="flex flex-wrap gap-2">
          {visibleActions.map((action) => (
            <ActionChip key={action} label={action} />
          ))}
        </div>
        {hasMoreActions ? (
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
            onClick={() => setShowAllActions((current) => !current)}
          >
            {showAllActions
              ? t('SHOW_LESS', { defaultValue: 'Show less' })
              : t('SEE_MORE', { defaultValue: 'See more' })}
          </button>
        ) : null}
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[min(92dvh,820px)] w-[calc(100vw-1.5rem)] max-w-[540px] flex-col gap-0 overflow-hidden p-0">
        {showSkeleton ? (
          <ConnectorDetailsSkeleton />
        ) : (
          <>
            <div className="shrink-0 px-7 pb-2 pt-7">
              <div className="flex items-start gap-4 pr-8">
                {isLogoPending ? (
                  <Skeleton className="h-11 w-11 shrink-0 rounded-full" />
                ) : (
                  <ConnectorLogo logo={logo} name={displayName} />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-0.5">
                      <h2 className="truncate text-xl font-semibold leading-tight tracking-tight text-foreground">
                        {displayName}
                      </h2>
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <p className="text-sm text-muted-foreground">{categoryLabel}</p>
                        <VisibilityBadge
                          value={getToolVisibility(toolDetails ?? tool)}
                          className="shrink-0"
                        />
                      </div>
                    </div>

                    {renderConnectionButton()}
                  </div>
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-7 pb-7 pt-3">
              {displayDescription ? (
                <p className="text-base leading-7 text-muted-foreground">{displayDescription}</p>
              ) : null}

              {primaryService ? (
                <div className="space-y-3">
                  {actionsHeading ? (
                    <p className="text-sm text-muted-foreground">{actionsHeading}</p>
                  ) : null}
                  {renderActionsContent()}
                </div>
              ) : null}

              {(primaryService || isPendingThis) && (
                <div className="space-y-4 border-t border-border/70 pt-5">
                  <p className="text-sm text-muted-foreground">{t('DETAILS', { defaultValue: 'Details' })}</p>

                  {primaryService ? (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">{t('APP', { defaultValue: 'App' })}</p>
                      <p className="text-sm font-semibold text-foreground">{appName || displayName}</p>
                    </div>
                  ) : null}

                  {isConnected && connectionEntry?.accountLabel ? (
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">
                        {t('CONNECTED_ACCOUNT', { defaultValue: 'Connected account' })}
                      </p>
                      <p className="text-sm font-semibold text-foreground">{connectionEntry.accountLabel}</p>
                    </div>
                  ) : null}
                </div>
              )}

              <p className="text-xs leading-5 text-muted-foreground/90">
                <span>
                  {t('CONNECTOR_DISCLAIMER', {
                    defaultValue:
                      'Third-party connectors are not built or maintained by Selise. Use caution when granting access to external services. Review the permissions before connecting. See our',
                  })}{' '}
                  <a
                    href="https://app.selise.ai/en/privacy-policy/"
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2 hover:text-foreground"
                  >
                    {t('PRIVACY_POLICY', { defaultValue: 'Privacy Policy' })}
                  </a>
                  .
                </span>
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
